import * as THREE from 'three';
import { Tank } from './Tank';
import type { TankInput } from '../core/InputController';
import {
  COLOR_PLAYER_BODY,
  COLOR_PLAYER_TURRET,
  PLAYER_SPEED_FWD,
  PLAYER_SPEED_BACK,
  PLAYER_TURN_SPEED,
  TURRET_TURN_SPEED,
  PLAYER_HP,
  FIRE_COOLDOWN,
  ARENA_HALF,
  TANK_BODY_WIDTH,
  TANK_BODY_DEPTH,
} from '../config';
import { Bullet } from './Bullet';

const DEG_TO_RAD = Math.PI / 180;

export class PlayerTank extends Tank {
  turretAngle = 0;
  cooldownTimer = 0;

  constructor() {
    super(COLOR_PLAYER_BODY, COLOR_PLAYER_TURRET, PLAYER_HP, PLAYER_SPEED_FWD);
  }

  reset(): void {
    this.hp = this.maxHp;
    this.alive = true;
    this.cooldownTimer = 0;
    this.turretAngle = 0;
    this.recoilAmount = 0;
    this.barrelGroup.position.z = 0;
    this.group.position.set(5, 0, 5);
    this.group.rotation.set(0, 0, 0);
    this.turretGroup.rotation.set(0, 0, 0);
    const mat = this.bodyMesh.material as THREE.MeshStandardMaterial;
    mat.opacity = 1;
    mat.transparent = false;
  }

  update(delta: number, input: TankInput, bullets: Bullet[]): void {
    if (!this.alive) return;

    this.cooldownTimer = Math.max(0, this.cooldownTimer - delta);
    this.updateRecoil(delta);

    // 车体旋转
    let isMoving = false;
    if (input.turn !== 0) {
      this.group.rotation.y += input.turn * PLAYER_TURN_SPEED * DEG_TO_RAD * delta;
      isMoving = true;
    }

    // 炮塔旋转
    if (input.turretTurn !== 0) {
      this.turretAngle += input.turretTurn * TURRET_TURN_SPEED * DEG_TO_RAD * delta;
    }
    this.turretGroup.rotation.y = this.turretAngle;

    // 移动
    let speedRatio = 0;
    if (input.forward !== 0) {
      const speed = input.forward > 0 ? this.moveSpeed : PLAYER_SPEED_BACK;
      speedRatio = input.forward * (speed / this.moveSpeed);
      const moveX = Math.sin(this.group.rotation.y) * input.forward * speed * delta;
      const moveZ = Math.cos(this.group.rotation.y) * input.forward * speed * delta;
      this.group.position.x += moveX;
      this.group.position.z += moveZ;
      isMoving = true;
    }

    // 履带轮动画
    this.updateWheels(delta, isMoving ? speedRatio : 0);

    // 开火
    if (input.fire && this.cooldownTimer <= 0) {
      this.fire(bullets);
    }
  }

  fire(bullets: Bullet[]): void {
    const muzzlePos = new THREE.Vector3();
    const dir = new THREE.Vector3();
    this.getMuzzleWorldPos(muzzlePos);
    this.getBarrelDirection(dir);

    const bullet = new Bullet(muzzlePos, dir, true);
    bullets.push(bullet);
    this.cooldownTimer = FIRE_COOLDOWN;
    this.triggerRecoil();
  }

  clampToArena(): void {
    const halfW = TANK_BODY_WIDTH / 2;
    const halfD = TANK_BODY_DEPTH / 2;
    this.group.position.x = THREE.MathUtils.clamp(
      this.group.position.x, -ARENA_HALF + halfW, ARENA_HALF - halfW,
    );
    this.group.position.z = THREE.MathUtils.clamp(
      this.group.position.z, -ARENA_HALF + halfD, ARENA_HALF - halfD,
    );
  }

  takeDamage(damage: number): boolean {
    if (!this.alive) return false;
    this.hp -= damage;
    const mat = this.bodyMesh.material as THREE.MeshStandardMaterial;
    mat.transparent = true;
    mat.opacity = 0.5;
    setTimeout(() => {
      if (this.alive) mat.opacity = 1;
    }, 150);
    if (this.hp <= 0) {
      this.hp = 0;
      this.alive = false;
      mat.transparent = true;
      mat.opacity = 0.3;
      return true;
    }
    return false;
  }
}
